import {skipToken} from '@reduxjs/toolkit/query';
import {FormField} from 'platform/components';
import {Box, Stack} from 'platform/foundation';

import {head, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {FALLBACK_LANGUAGE} from '../../../i18n/fallbackLanguage';
import i18n from '../../../i18n/i18n';
import {useGetMakeModelQuery} from '../../../store/catalogueApi';
import {VEHICLETYPE_PASSENGER_CAR} from '../constants/vehiclePassengerCar';
import {VehicleFormType} from '../types/VehicleFormType';
import {useCreatePageContext} from './CreatePageContext';

interface MakeModelFieldsProps extends TestIdProps {
  onFilterFieldChange?: (changedFieldName: keyof VehicleFormType) => void;
}

export function MakeModelFields(props: MakeModelFieldsProps) {
  const {
    control,
    values: {make},
  } = useCreatePageContext();
  const language = i18n?.resolvedLanguage ?? FALLBACK_LANGUAGE;

  const {data: makes, isLoading: isMakesLoading} = useGetMakeModelQuery({
    vehicleType: VEHICLETYPE_PASSENGER_CAR,
    lang: [language],
  });
  const {data: models, isLoading: isModelsLoading} = useGetMakeModelQuery(
    isNotNil(make)
      ? {
          vehicleType: VEHICLETYPE_PASSENGER_CAR,
          make,
          lang: [language],
        }
      : skipToken,
    {
      selectFromResult: (result) => ({
        ...result,
        data: head(result?.data ?? [])?.models,
      }),
    }
  );

  const makeOptions = makes?.map((make) => ({
    label: make.default_label,
    value: make.make,
  }));

  const modelOptions = models?.map((model) => ({
    label: model.default_label,
    value: model.model,
  }));

  return (
    <Stack direction={['column', 'column', 'row', 'row']} spacing={6}>
      <Box flex={1}>
        <FormField
          control={control}
          type="choice"
          name="make"
          label={i18n.t('page.create.labels.make')}
          isRequired
          options={makeOptions ?? []}
          isLoading={isMakesLoading}
          onChange={() => props.onFilterFieldChange?.('make')}
          menuInPortal
          data-testid={suffixTestId('form-make', props)}
        />
      </Box>
      <Box flex={1}>
        <FormField
          control={control}
          type="choice"
          name="modelFamily"
          label={i18n.t('page.create.labels.model')}
          isRequired
          options={modelOptions ?? []}
          isDisabled={isNilOrEmpty(make)}
          isLoading={isModelsLoading}
          onChange={() => props.onFilterFieldChange?.('modelFamily')}
          menuInPortal
          data-testid={suffixTestId('form-modelFamily', props)}
        />
      </Box>
    </Stack>
  );
}
